import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PermissionGate } from "../../components/auth/PermissionGate";
import { EmptyState } from "../../components/common/EmptyState";
import { TableSkeletonRows } from "../../components/common/LoadingSkeletons";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { showToast } from "../../lib/toast";
import { Customer, customerService } from "../../services/customer.service";

export function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: string;
    name: string;
  }>({
    isOpen: false,
    id: "",
    name: "",
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const limit = 20;

  useEffect(() => {
    loadCustomers();
  }, [page]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerService.getCustomers({
        page,
        limit,
        search: searchTerm || undefined,
      });
      setCustomers(response.data || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load customers",
      );
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadCustomers();
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteModal({ isOpen: true, id, name });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await customerService.deleteCustomer(deleteModal.id);
      showToast.success("Customer deleted successfully");
      loadCustomers();
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to delete customer",
      );
    } finally {
      setIsDeleting(false);
      setDeleteModal({ isOpen: false, id: "", name: "" });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm text-gray-500 mt-1">
            {total} total customer{total !== 1 ? "s" : ""}
          </p>
        </div>
        <PermissionGate permission="customers:create">
          <button
            onClick={() => navigate("/customers/create")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Add Customer
          </button>
        </PermissionGate>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search by name, phone, or email..."
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button>
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm("");
                setPage(1);
                setTimeout(loadCustomers, 0);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  "Name",
                  "Contact",
                  "Address",
                  "Credit",
                  "Loyalty",
                  "Actions",
                ].map((h) => (
                  <th key={h} className="px-4 py-3">
                    <div className="h-3 w-16 rounded bg-slate-200 animate-pulse" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <TableSkeletonRows rows={8} cols={6} />
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && customers.length === 0 && (
        <EmptyState
          icon="users"
          title="No customers found"
          description="Start building your customer base by adding your first customer."
          action={{
            label: "Add customer",
            onClick: () => navigate("/customers/create"),
          }}
        />
      )}

      {/* Customers Table */}
      {!loading && customers.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Credit Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Loyalty Points
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {customer.name}
                      </div>
                      {customer.gstin && (
                        <div className="text-xs text-gray-500">
                          GSTIN: {customer.gstin}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {customer.phone}
                      </div>
                      {customer.email && (
                        <div className="text-xs text-gray-500">
                          {customer.email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {customer.address || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {customer.creditLimit ? (
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            ₹{Number(customer.creditLimit).toFixed(0)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {customer.creditDays || 0} days
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">Cash only</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {(customer.loyaltyPoints ?? 0) > 0 ? (
                        <div className="text-sm font-medium text-amber-700">
                          ⭐ {customer.loyaltyPoints}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">—</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <PermissionGate permission="customers:update">
                          <button
                            onClick={() =>
                              navigate(`/customers/${customer.id}/edit`)
                            }
                            className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                          >
                            Edit
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="customers:delete">
                          <button
                            onClick={() =>
                              handleDeleteClick(customer.id, customer.name)
                            }
                            className="text-red-600 hover:text-red-900 font-medium text-sm"
                          >
                            Delete
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(page - 1) * limit + 1} to{" "}
                {Math.min(page * limit, total)} of {total} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                  className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: "", name: "" })}
        onConfirm={handleDeleteConfirm}
        title="Delete Customer"
        message={`Are you sure you want to delete customer "${deleteModal.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
